import { useCallback, useMemo } from 'react';
import { useUnits } from '../context/UnitsContext';
import { useExchangeRates } from '../context/ExchangeRatesContext';
import {
  formatCurrency,
  getCurrencyForLoanCountry,
} from './utils';
import { useI18n } from '../i18n';

/** Match backend resolve_property_currency */
export function getPropertyCurrency(property) {
  if (!property) return 'USD';
  const explicit = (property.currency || '').toUpperCase();
  if (['USD', 'COP', 'EUR', 'AUD', 'CAD'].includes(explicit)) return explicit;

  const lc = property.financing?.loan_country || property.loan_country;
  if (lc) {
    const fromLoan = getCurrencyForLoanCountry(lc);
    if (['USD', 'COP', 'EUR', 'AUD', 'CAD'].includes(fromLoan)) return fromLoan;
  }

  const addr = (property.address || '').toLowerCase();
  if (
    addr.includes('colombia') ||
    addr.includes('bogot') ||
    addr.includes('medell') ||
    addr.includes('cali') ||
    addr.includes(', co')
  ) {
    return 'COP';
  }

  return 'USD';
}

export function convertAmount(amount, fromCurrency, toCurrency, rateSnapshot) {
  const from = (fromCurrency || 'USD').toUpperCase();
  const to = (toCurrency || 'USD').toUpperCase();
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  if (from === to) return n;

  const table = rateSnapshot?.to_usd;
  if (!table) return n;

  const fromFactor = table[from];
  const toFactor = table[to];
  if (fromFactor == null || toFactor == null) return n;

  const usd = n * fromFactor;
  return usd / toFactor;
}

/** Build fmt/fmtStat helpers that convert from native currency into display currency. */
export function createMoneyFormatters({
  displayCurrency,
  language,
  convert,
  getPropertyCurrency: getPropCurrency = getPropertyCurrency,
  defaultFromCurrency = null,
}) {
  const toDisplay = (amount, fromCurrency) => {
    const from = (fromCurrency ?? defaultFromCurrency ?? displayCurrency).toUpperCase();
    if (from === displayCurrency) return Number(amount) || 0;
    return convert(amount, from, displayCurrency);
  };

  return {
    displayCurrency,
    toDisplay,
    fmt: (amount, fromCurrency) =>
      formatCurrency(toDisplay(amount, fromCurrency), { currency: displayCurrency, language }),
    fmtStat: (amount, fromCurrency) =>
      formatCurrency(toDisplay(amount, fromCurrency), { currency: displayCurrency, language }),
    fmtNative: (amount, fromCurrency) => {
      const cur = (fromCurrency ?? defaultFromCurrency ?? displayCurrency).toUpperCase();
      return formatCurrency(amount, { currency: cur, language });
    },
    fmtProp: (prop, field) => {
      const from = prop.currency || getPropCurrency(prop);
      return formatCurrency(toDisplay(prop[field], from), { currency: displayCurrency, language });
    },
    fmtPropFull: (prop, field) => {
      const from = prop.currency || getPropCurrency(prop);
      return formatCurrency(prop[field], { currency: from, language });
    },
    fmtPropTitle: (prop, field) => {
      const from = prop.currency || getPropCurrency(prop);
      const native = formatCurrency(prop[field], { currency: from, language });
      if (from === displayCurrency) return native;
      const converted = formatCurrency(toDisplay(prop[field], from), {
        currency: displayCurrency,
        language,
      });
      return `${converted} (${native})`;
    },
  };
}

/**
 * Format amounts stored in a property's native currency into the user's display currency (TRM for COP↔USD).
 */
export function useMoneyFormatting() {
  const { language } = useI18n();
  const { currency: displayCurrency } = useUnits();
  const { convert, rates, loading: ratesLoading, trmCopPerUsd, ratesAsOf, getPropertyCurrency } =
    useExchangeRates();

  const toDisplay = useCallback(
    (amount, fromCurrency) => {
      const from = (fromCurrency || displayCurrency).toUpperCase();
      const n = Number(amount) || 0;
      if (from === displayCurrency) return n;
      if (!rates?.to_usd) return n;
      return convert(amount, from, displayCurrency);
    },
    [convert, displayCurrency, rates],
  );

  const formatters = useMemo(
    () =>
      createMoneyFormatters({
        displayCurrency,
        language,
        convert,
        getPropertyCurrency,
      }),
    [displayCurrency, language, convert, getPropertyCurrency],
  );

  const needsConversion = useCallback(
    (fromCurrency) => {
      const from = (fromCurrency || displayCurrency).toUpperCase();
      return from !== displayCurrency && Boolean(rates?.to_usd);
    },
    [displayCurrency, rates],
  );

  return useMemo(
    () => ({
      displayCurrency,
      language,
      rates,
      ratesLoading,
      trmCopPerUsd,
      ratesAsOf,
      convert,
      toDisplay,
      formatMoney: (amount, opts = {}) =>
        formatters.fmt(amount, opts.fromCurrency),
      formatMoneyStat: (amount, opts = {}) =>
        formatters.fmtStat(amount, opts.fromCurrency),
      formatMoneyResponsive: (amount, opts = {}) =>
        formatters.fmt(amount, opts.fromCurrency),
      formatMoneyNative: formatters.fmtNative,
      formatPropMoney: formatters.fmtProp,
      formatPropTitle: formatters.fmtPropTitle,
      getPropertyCurrency,
      needsConversion,
    }),
    [
      displayCurrency,
      language,
      rates,
      ratesLoading,
      trmCopPerUsd,
      ratesAsOf,
      convert,
      toDisplay,
      formatters,
      getPropertyCurrency,
      needsConversion,
    ],
  );
}
